import get from 'lodash/get';
import { normalizeMemberDetailsIoStatus } from '../utils/memberDetailsIoStatus';

export const isMemberDetailsSubsection = (activeSubsectionId = '') => (
    String(activeSubsectionId).toLowerCase() === 'memberdetails'
);

export const AGENT_EMAIL_MISMATCH_MESSAGE = (
    'The Insured\'s email address cannot be the same as the agent\'s email address.'
);

export const DOB_AGE_MESSAGE = 'Outside of age parameters. Please verify DOB.';

export const isBlankAnswer = (value) => {
    if (value === 0 || value === false) {
        return false;
    }
    if (value == null || value === '') {
        return true;
    }
    if (typeof value === 'string') {
        return value.trim() === '';
    }
    if (Array.isArray(value)) {
        return value.length === 0;
    }
    if (typeof value === 'object') {
        if (Object.prototype.hasOwnProperty.call(value, 'id')) {
            return value.id == null || String(value.id).trim() === '';
        }
        return Object.keys(value).length === 0;
    }
    return false;
};

const MIN_DOB_AGE = 18;
const MAX_DOB_AGE = 84;

const parseDobDate = (raw) => {
    const s = String(raw ?? '').trim();
    if (!s) {
        return null;
    }

    let year;
    let month;
    let day;

    if (/^\d{4}-\d{2}-\d{2}$/.test(s)) {
        [year, month, day] = s.split('-').map((part) => Number(part));
    } else if (/^\d{1,2}\/\d{1,2}\/\d{4}$/.test(s)) {
        const parts = s.split('/');
        month = Number(parts[0]);
        day = Number(parts[1]);
        year = Number(parts[2]);
    } else {
        return null;
    }

    if (!year || !month || !day) {
        return null;
    }

    const date = new Date(year, month - 1, day);
    if (
        date.getFullYear() !== year
        || date.getMonth() !== month - 1
        || date.getDate() !== day
    ) {
        return null;
    }

    return date;
};

const ageFromDob = (dob) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const birth = new Date(dob.getTime());
    birth.setHours(0, 0, 0, 0);

    let age = today.getFullYear() - birth.getFullYear();
    const monthDiff = today.getMonth() - birth.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
        age -= 1;
    }

    return age;
};

/**
 * @returns {{ isError: boolean, questionStatusMessage: string }}
 */
export const getMemberDobAgeValidation = (dobRaw) => {
    const dob = parseDobDate(dobRaw);
    if (!dob) {
        return { isError: false, questionStatusMessage: '' };
    }

    const age = ageFromDob(dob);
    if (age < MIN_DOB_AGE || age > MAX_DOB_AGE) {
        return { isError: true, questionStatusMessage: DOB_AGE_MESSAGE };
    }

    return { isError: false, questionStatusMessage: '' };
};

// Mirrors AccidentMemberDetailsValidation::WHITESPACE_AS_EMPTY_FIELD_IDS (backend).
export const MEMBER_WHITESPACE_REQUIRED_QUESTION_IDS = new Set([
    'MemberFirstName',
    'MemberLastName',
    'MemberStreetAddress',
    'MemberAddress2',
    'MemberCity',
    'MemberEmailAddress',
]);

/**
 * Shared required-field check for live FE validation (whitespace text + selections).
 * @returns {{ isError: boolean, questionStatusMessage: string }}
 */
export const getRequiredAnswerValidation = (response, question) => {
    const isRequired = get(question, 'validations.required.value', false);
    if (!isRequired) {
        return { isError: false, questionStatusMessage: '' };
    }

    if (isBlankAnswer(response)) {
        return {
            isError: true,
            questionStatusMessage: get(question, 'validations.required.error_message', ''),
        };
    }

    return { isError: false, questionStatusMessage: '' };
};

export const isAccidentProduct = (state) => {
    const planCode = String(get(state, 'questions.otherParams.offerPlanCode', ''))
        .trim().toLowerCase();
    const productType = String(get(state, 'questions.otherParams.offerProductType', ''))
        .trim().toLowerCase();
    return planCode === 'ioa21' || productType === 'accident';
};

/**
 * Returns true for products that support MemberJoinMonth/MemberJoinYear on
 * the Member Details page (Accident, ULP/Term).
 */
export const isJoinDateProduct = (state) => {
    if (isAccidentProduct(state)) return true;
    // ULP / Term (IOT21)
    const planCode = String(get(state, 'questions.otherParams.offerPlanCode', ''))
        .trim().toLowerCase();
    return planCode === 'iot21';
};

export const getAgentEmailFromState = (state) => (
    String(
        get(state, 'agentParams.agentEmail', '')
        || get(state, 'questions.otherParams.agentEmail', ''),
    ).trim().toLowerCase()
);

/**
 * @returns {{ isError: boolean, questionStatusMessage: string }}
 */
export const getMemberEmailAgentValidation = (memberEmailRaw, state) => {
    const memberEmail = String(memberEmailRaw ?? '').trim().toLowerCase();
    const agentEmail = getAgentEmailFromState(state);

    if (memberEmail !== '' && agentEmail !== '' && memberEmail === agentEmail) {
        return { isError: true, questionStatusMessage: AGENT_EMAIL_MISMATCH_MESSAGE };
    }

    return { isError: false, questionStatusMessage: '' };
};

export const AAA_MEMBERSHIP_NUMBER_QUESTION_ID = 'AAAMembershipNumber';

export const isAaaMembershipNumberQuestionId = (questionId) => (
    questionId === AAA_MEMBERSHIP_NUMBER_QUESTION_ID
);

export const isMemberJoinDateQuestionId = (questionId) => (
    questionId === 'MemberJoinMonth' || questionId === 'MemberJoinYear'
);

// Matches crux mod10CheckDigit — AAA only; reflexive API has no backend checksum.
export const isInvalidAaaMembershipChecksum = (value) => {
    const numbers = String(value ?? '').trim();
    if (!numbers) {
        return false;
    }
    if (numbers.length < 16 || numbers.replace(/0/g, '') === '') {
        return true;
    }
    const reverseNumber = numbers.split('').reverse();
    const checkDigit = parseInt(reverseNumber[0], 10);
    let doubledSum = 0;
    for (let i = 1; i < reverseNumber.length; i += 1) {
        let double = parseInt(reverseNumber[i], 10);
        if (i % 2 !== 0) {
            double *= 2;
            double = double > 9 ? double - 9 : double;
        }
        doubledSum += double;
    }
    return (doubledSum * 9) % 10 !== checkDigit;
};

export const isCompleteAaaMembershipNumber = (value) => {
    const trimmed = String(value ?? '').trim();
    return /^\d{16}$/.test(trimmed) && !isInvalidAaaMembershipChecksum(trimmed);
};

/**
 * Live + API-patch validation for AAAMembershipNumber (crux checksum on type, ASGIWL-style).
 * @returns {{ isError: boolean, questionStatusMessage: string }}
 */
export const getAaaMembershipNumberLiveValidation = (response, question) => {
    const checksumMessage = get(question, 'validations.checksum.error_message', '');
    const minLengthMessage = get(question, 'validations.min_length.error_message', '');
    const membershipErrorMessage = checksumMessage || minLengthMessage;
    const isRequired = get(question, 'validations.required.value', false) === true;
    const value = String(response ?? '').trim();

    if (!value) {
        if (!isRequired) {
            return { isError: false, questionStatusMessage: '' };
        }
        return { isError: false, questionStatusMessage: '' };
    }

    if (value.length < 16 || isInvalidAaaMembershipChecksum(value)) {
        if (!membershipErrorMessage) {
            return { isError: value.length > 0, questionStatusMessage: '' };
        }
        return { isError: true, questionStatusMessage: membershipErrorMessage };
    }

    return { isError: false, questionStatusMessage: '' };
};

const getAaaMembershipValidationMessages = (question) => {
    const messages = [
        get(question, 'validations.checksum.error_message', ''),
        get(question, 'validations.min_length.error_message', ''),
    ].filter(Boolean);
    return new Set(messages);
};

export const patchAaaMembershipChecksumQuestionStatus = (question) => {
    if (!question || question.question_id !== AAA_MEMBERSHIP_NUMBER_QUESTION_ID) {
        return question;
    }

    const validation = getAaaMembershipNumberLiveValidation(question.response, question);
    if (validation.isError) {
        return {
            ...question,
            question_status: 'invalid',
            question_status_message: validation.questionStatusMessage,
        };
    }

    const statusMessage = String(question.question_status_message ?? '').trim();
    if (
        question.question_status === 'invalid'
        && (statusMessage === '' || getAaaMembershipValidationMessages(question).has(statusMessage))
    ) {
        return {
            ...question,
            question_status: 'valid',
            question_status_message: '',
        };
    }

    return question;
};

export const walkAndPatchAaaMembershipChecksum = (questions) => {
    if (!Array.isArray(questions)) {
        return questions;
    }
    return questions.map((question) => {
        let patched = patchAaaMembershipChecksumQuestionStatus(question);
        if (Array.isArray(patched.questions) && patched.questions.length > 0) {
            patched = {
                ...patched,
                questions: walkAndPatchAaaMembershipChecksum(patched.questions),
            };
        }
        return patched;
    });
};

// Mirrors MemberDetailsIoService::QUESTION_TO_API_FIELD keys (backend).
export const VERIFY_MEMBER_QUESTION_IDS = new Set([
    'AAAMembershipNumber',
    'MemberJoinMonth',
    'MemberJoinYear',
    'MemberFirstName',
    'MemberMiddleInitial',
    'MemberLastName',
    'MemberDateOfBirth',
    'MemberGender',
    'MemberStreetAddress',
    'MemberAddress2',
    'MemberCity',
    'MemberState',
    'MemberZIPCode',
    'MemberPhoneType',
    'MemberPhoneNumber',
    'MemberEmailAddress',
]);

export const getCombinedSectionQuestions = ({
    leftQuestionList = [],
    questionList = [],
} = {}) => [
    ...(Array.isArray(leftQuestionList) ? leftQuestionList : []),
    ...(Array.isArray(questionList) ? questionList : []),
];

export const isVerifyMemberInfoSection = (questionList = []) => (
    Array.isArray(questionList)
    && questionList.some((q) => VERIFY_MEMBER_QUESTION_IDS.has(q.question_id))
);

export const SECONDARY_ADDRESSEE_SECTION_IDS = new Set([
    'SecondaryAddresseeGroup',
    'DesignateSecondaryAddressee',
]);

export const SECONDARY_ADDRESSEE_QUESTION_IDS = new Set([
    'DesignateSecondaryAddressee',
    'SecFirstName',
    'SecMiddleInitial',
    'SecLastName',
    'SecStreetAddress',
    'SecAddress2',
    'SecCity',
    'SecState',
    'SecZIPCode',
    'SecPhoneType',
    'SecPhoneNumber',
    'SecEmailAddress',
]);

export const isSecondaryAddresseeSection = (questionList = []) => {
    if (!Array.isArray(questionList)) {
        return false;
    }
    return flattenQuestionTree(questionList).some(
        (q) => SECONDARY_ADDRESSEE_SECTION_IDS.has(q.question_id),
    );
};

const isSecondaryUspsValidationTarget = (target) => {
    const normalized = String(target ?? '').trim().toLowerCase();
    return normalized === 'secondary' || normalized === 'secondary addressee';
};

export const resolveMemberDetailsIoBlockingUserMessage = (
    memberDetailsIo = {},
    payloadErrors = {},
) => {
    const requestStatus = normalizeMemberDetailsIoStatus(memberDetailsIo.requestOffersStatus);
    const ioStatus = normalizeMemberDetailsIoStatus(memberDetailsIo.ioDataUpdateStatus);
    const uspsStatus = normalizeMemberDetailsIoStatus(memberDetailsIo.uspsValidationStatus);

    if (uspsStatus === 'blocked') {
        const uspsMessage = String(memberDetailsIo.uspsMessage ?? '').trim();
        if (uspsMessage !== '') {
            return uspsMessage;
        }
    }

    if (
        (requestStatus === 'no_offer' || requestStatus === 'error')
        && memberDetailsIo.requestOffersMessage
    ) {
        return memberDetailsIo.requestOffersMessage;
    }

    if (ioStatus === 'error' && memberDetailsIo.ioDataUpdateMessage) {
        return memberDetailsIo.ioDataUpdateMessage;
    }

    return (
        payloadErrors.userMessage
        || payloadErrors.message
        || 'Unable to continue. Please try again.'
    );
};

export const getMemberDetailsIoBlockingTarget = (memberDetailsIo = {}) => {
    if (!memberDetailsIo?.blocking) {
        return null;
    }

    const requestStatus = normalizeMemberDetailsIoStatus(memberDetailsIo.requestOffersStatus);
    if (requestStatus === 'no_offer' || requestStatus === 'error') {
        return 'verify';
    }

    if (normalizeMemberDetailsIoStatus(memberDetailsIo.uspsValidationStatus) === 'blocked') {
        return isSecondaryUspsValidationTarget(memberDetailsIo.uspsValidationTarget)
            ? 'secondary'
            : 'verify';
    }

    return 'verify';
};

export const resolveIoSectionTarget = (memberDetailsIo = {}) => {
    const blockingTarget = getMemberDetailsIoBlockingTarget(memberDetailsIo);
    if (blockingTarget) {
        return blockingTarget;
    }
    if (isSecondaryUspsValidationTarget(memberDetailsIo?.uspsValidationTarget)) {
        return 'secondary';
    }
    return 'verify';
};

export const isIoTargetSection = (section, memberDetailsIo = {}) => {
    const target = resolveIoSectionTarget(memberDetailsIo);
    const combinedQuestions = getCombinedSectionQuestions(section);
    if (target === 'secondary') {
        return isSecondaryAddresseeSection(combinedQuestions);
    }
    return isVerifyMemberInfoSection(combinedQuestions);
};

export const isIoBlockingOwnerSection = (section, memberDetailsIo = {}) => (
    !!memberDetailsIo?.blocking && isIoTargetSection(section, memberDetailsIo)
);

export const findIoTargetSectionIndex = (sections = [], memberDetailsIo = {}) => {
    if (!Array.isArray(sections)) {
        return -1;
    }
    return sections.findIndex((section) => isIoTargetSection(section, memberDetailsIo));
};

export const findIoBlockingOwnerSectionIndex = (sections = [], memberDetailsIo = {}) => {
    if (!memberDetailsIo?.blocking) {
        return -1;
    }
    return findIoTargetSectionIndex(sections, memberDetailsIo);
};

export const getMemberDetailsIoTrackedQuestionIds = (memberDetailsIo = {}) => (
    resolveIoSectionTarget(memberDetailsIo) === 'secondary'
        ? SECONDARY_ADDRESSEE_QUESTION_IDS
        : VERIFY_MEMBER_QUESTION_IDS
);

export const shouldClearIoBlockingForFieldEdit = (section, questionId, memberDetailsIo = {}) => {
    if (!memberDetailsIo?.blocking || !questionId) {
        return false;
    }
    if (!isIoTargetSection(section, memberDetailsIo)) {
        return false;
    }
    return getMemberDetailsIoTrackedQuestionIds(memberDetailsIo).has(questionId);
};

export const sectionIoErrorsFromPayload = (section, payloadErrors = {}, memberDetailsIo = {}) => {
    if (!payloadErrors?.message && !payloadErrors?.userMessage) {
        return {};
    }
    if (!isIoTargetSection(section, memberDetailsIo)) {
        return {};
    }
    const message = payloadErrors.message || payloadErrors.userMessage || '';
    return message ? { message } : {};
};

export const mergePreservedMemberDetailsIoOtherParams = (
    previousOtherParams = {},
    incomingOtherParams = {},
) => {
    const merged = { ...previousOtherParams, ...incomingOtherParams };
    const hasActiveBlock = !!previousOtherParams.member_details_io?.blocking;

    if (hasActiveBlock && !incomingOtherParams.member_details_io && previousOtherParams.member_details_io) {
        merged.member_details_io = previousOtherParams.member_details_io;
    }

    if (
        hasActiveBlock
        && !incomingOtherParams.member_details_io_failed_snapshot
        && previousOtherParams.member_details_io_failed_snapshot
    ) {
        merged.member_details_io_failed_snapshot = previousOtherParams.member_details_io_failed_snapshot;
    }

    return merged;
};

export const preserveMemberDetailsIoSectionErrors = (
    previousSections = [],
    nextSections = [],
    memberDetailsIo = {},
) => {
    if (!memberDetailsIo?.blocking || !Array.isArray(previousSections) || !Array.isArray(nextSections)) {
        return nextSections;
    }

    return nextSections.map((section, index) => {
        const previousSection = previousSections[index];
        const previousErrors = previousSection?.errors;
        if (!previousErrors?.title && !previousErrors?.message) {
            return section;
        }
        if (!isIoBlockingOwnerSection(section, memberDetailsIo)) {
            return section;
        }

        const { title, ...errorsWithoutTitle } = previousErrors;
        return { ...section, errors: { ...errorsWithoutTitle } };
    });
};

const flattenQuestionTree = (questions, result = []) => {
    if (!Array.isArray(questions)) {
        return result;
    }
    questions.forEach((question) => {
        if (!question || typeof question !== 'object') {
            return;
        }
        result.push(question);
        if (Array.isArray(question.questions) && question.questions.length > 0) {
            flattenQuestionTree(question.questions, result);
        }
    });
    return result;
};

export const collectMemberDetailsAnswersFromState = (state) => {
    const activeSubsectionId = get(state, 'questions.activeSubsectionId', '');
    if (!isMemberDetailsSubsection(activeSubsectionId)) {
        return [];
    }
    const sectionList = get(state, `questions.sections.${activeSubsectionId}`, []);
    if (!Array.isArray(sectionList)) {
        return [];
    }

    const flatQuestions = [];
    sectionList.forEach((section) => {
        flattenQuestionTree(getCombinedSectionQuestions(section), flatQuestions);
    });

    return flatQuestions
        .filter((question) => question.question_id)
        .map(({ question_id: questionId, response }) => ({
            question_id: questionId,
            answer: response,
        }));
};

export const normalizeMemberAnswerForSnapshot = (questionId, response) => {
    if (response == null || response === '') {
        return '';
    }
    if (typeof response === 'object') {
        if (Object.prototype.hasOwnProperty.call(response, 'id')) {
            return String(response.id ?? '').trim();
        }
        if (
            Object.prototype.hasOwnProperty.call(response, 'year')
            || Object.prototype.hasOwnProperty.call(response, 'month')
            || Object.prototype.hasOwnProperty.call(response, 'day')
        ) {
            const { year, month, day } = response;
            if (year != null && month != null && day != null) {
                return `${String(month).padStart(2, '0')}/${String(day).padStart(2, '0')}/${year}`;
            }
        }
        return JSON.stringify(response);
    }
    if (typeof response === 'string') {
        const trimmed = response.trim();
        if (questionId === 'MemberDateOfBirth') {
            const dob = parseDobDate(trimmed);
            if (dob) {
                return `${String(dob.getMonth() + 1).padStart(2, '0')}/${String(dob.getDate()).padStart(2, '0')}/${dob.getFullYear()}`;
            }
        }
        return trimmed;
    }
    return String(response);
};

export const buildAnswerSnapshotForQuestions = (answersArray = [], questionIds = new Set()) => {
    const snapshot = {};
    if (!Array.isArray(answersArray)) {
        return snapshot;
    }
    answersArray.forEach(({ question_id: questionId, answer }) => {
        if (questionIds.has(questionId)) {
            snapshot[questionId] = normalizeMemberAnswerForSnapshot(questionId, answer);
        }
    });
    return snapshot;
};

export const buildMemberDetailsIoFailedSnapshot = (answersArray = [], memberDetailsIo = {}) => (
    buildAnswerSnapshotForQuestions(
        answersArray,
        getMemberDetailsIoTrackedQuestionIds(memberDetailsIo),
    )
);

export const memberAnswerDiffersFromSnapshot = (snapshot, questionId, response) => {
    if (!snapshot || !questionId) {
        return false;
    }
    const current = normalizeMemberAnswerForSnapshot(questionId, response);
    const previous = snapshot[questionId];
    if (previous === undefined) {
        return current !== '';
    }
    return current !== previous;
};
